import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/v2/ui/Dialog'
import { Button } from '@/components/v2/ui'
import { cn } from '@/lib/utils'
import type { SlotGeneration } from '@/lib/api/amazon-listing.types'
import { downloadSlotGeneration, triggerBlobDownload } from '@/lib/api/amazon-listing'
import { useTranslation } from 'react-i18next'

interface SlotGenerationLightboxProps {
  slotId: string
  generations: SlotGeneration[]
  currentIndex: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: (index: number) => void
}

export function SlotGenerationLightbox({
  slotId,
  generations,
  currentIndex,
  open,
  onOpenChange,
  onNavigate,
}: SlotGenerationLightboxProps) {
  const { t } = useTranslation()
  const gen = generations[currentIndex]
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < generations.length - 1

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && hasPrev) onNavigate(currentIndex - 1)
    if (e.key === 'ArrowRight' && hasNext) onNavigate(currentIndex + 1)
  }

  const handleDownload = async () => {
    if (!gen) return
    try {
      const blob = await downloadSlotGeneration(slotId, gen.id)
      triggerBlobDownload(blob, `generation-${gen.id}.png`)
    } catch { /* silent */ }
  }

  if (!gen) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[95vh] p-0 bg-black/95 border-neutral-800"
        onKeyDown={handleKeyDown}
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">
          {t('amazonListing.lightboxTitle', { current: currentIndex + 1, total: generations.length })}
        </DialogTitle>

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={t('common.close')}
        >
          <X className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={handleDownload}
          className="absolute top-3 right-12 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={t('common.download')}
        >
          <Download className="w-4 h-4" />
        </button>

        <div className="relative flex items-center justify-center min-h-[60vh]">
          <img
            src={gen.asset_url}
            alt=""
            className="max-w-full max-h-[85vh] object-contain"
          />
        </div>

        {hasPrev && (
          <div className="absolute inset-y-0 left-0 flex items-center p-3">
            <Button variant="ghost" size="icon" onClick={() => onNavigate(currentIndex - 1)}
              className="bg-black/50 hover:bg-black/70 text-white" aria-label={t('amazonListing.prevImage')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
          </div>
        )}
        {hasNext && (
          <div className="absolute inset-y-0 right-0 flex items-center p-3">
            <Button variant="ghost" size="icon" onClick={() => onNavigate(currentIndex + 1)}
              className="bg-black/50 hover:bg-black/70 text-white" aria-label={t('amazonListing.nextImage')}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        )}

        <div className={cn('absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between')}>
          {gen.fidelity_score != null && (
            <span className="text-white/70 text-xs">Fidelity: {gen.fidelity_score}%</span>
          )}
          <span className="text-white/50 text-xs ml-auto">
            {currentIndex + 1} / {generations.length}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
